/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';

import { Button, CheckboxGroup, ContextualHelp, Search, Typography2 } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronUpIcon } from '@coursera/cds-icons';

import useDebounce from 'bundles/common/hooks/useDebounce';
import { useEnterpriseAdminSearchContext } from 'bundles/enterprise-admin-product-search/context';
import TrackedButton from 'bundles/page/components/TrackedButton';
import CourseraPlusSearchFilter from 'bundles/search-common/components/filters/CourseraPlusSearchFilter';
import SearchFilterItem from 'bundles/search-common/components/filters/SearchFilterItem';
import type { FilterConfig } from 'bundles/search-common/components/filters/getFiltersList';
import useSortedItems from 'bundles/search-common/components/filters/useSortedItems';
import {
  ENTERPRISE_COLLECTION_ENTITIES,
  THRESHOLD_FOR_FILTER_GROUP_BECOMING_SEARCHABLE,
} from 'bundles/search-common/constants';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import type { SearchFilterEventingData, SearchFilterOption } from 'bundles/search-common/types';
import { removeWhiteSpace } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

export const FILTER_PREVIEW_COUNT = 4;

export type UnappliedFiltersByFacet = {
  [facetName: string]: string[];
};

type Props = {
  groupConfig: FilterConfig;
  groupValues: SearchFilterOption[];
  eventingData: SearchFilterEventingData;
  onFilterChange: (unappliedFilters: UnappliedFiltersByFacet) => void;
  unappliedFilters: string[];
  isChip?: boolean;
  shouldHideGroupLabel?: boolean;
};

const styles = {
  search: css`
    margin: 8px 0 12px;

    .cds-input-root {
      width: 100%;
    }
  `,
  checkboxGroup: css`
    width: 100%;
  `,
  showMoreButton: css`
    width: fit-content;
  `,
};

function filterFilterOptions(
  searchInputValue: string,
  isEnterpriseAdminSearch: boolean
): (value: SearchFilterOption, index: number, array: SearchFilterOption[]) => boolean {
  return (item) => {
    const itemMatchesSearchQuery =
      !searchInputValue || item.label.toLowerCase().includes(searchInputValue.toLowerCase());
    const productTypeIncludedInEnterprise =
      !isEnterpriseAdminSearch || (isEnterpriseAdminSearch && !ENTERPRISE_COLLECTION_ENTITIES.includes(item.label));
    return itemMatchesSearchQuery && productTypeIncludedInEnterprise;
  };
}

const getCustomFilterGroupLabel = (): Record<string, string> => ({
  productTypeDescription: _t('Product type'),
  productDifficultyLevel: _t('Difficulty level'),
});

// Disabling because the below as it's cleaner not to refactor into a component and renderLabe is expecting (labelElement: React.ReactElement) => React.ReactElement;
// eslint-disable-next-line react/display-name
const getFilterGroupTooltipRenderer = (group: FilterConfig) => (labelComponent: React.ReactElement) =>
  (
    <ContextualHelp
      helpToggleProps={{
        'aria-label': _t('Information about this filter group'),
      }}
      label={labelComponent}
    >
      {/* Should the experimental variants be successful, move this to FILTERS_CONFIG */}
      {group.property === 'productTypeDescription' ? (
        <FormattedHTMLMessage
          message={_t(`
          <p><strong>Courses:</strong> Learn from top instructors with graded assignments, videos, and discussion forums.</p>
          <p><strong>Professional certificate:</strong> Earn career credentials from industry leaders that demonstrate your expertise.</p>
          <p><strong>Specializations:</strong> Get in-depth knowledge of a subject by completing a series of courses and projects.</p>
          <p><strong>Degrees:</strong> Earn your Bachelor’s or Master’s degree online for a fraction of the cost of in-person learning.</p>
          <p><strong>Projects:</strong> Learn job-relevant skills in under 2 hours with interactive, hands-on tutorials.</p>
          <p><strong>Postgraduate & Graduate Diplomas:</strong> Take graduate-level courses without getting a full degree, or earn a university-issued career credential online.</p>
        `)}
        />
      ) : (
        <Typography2 component="p">{group?.getToolTip?.()}</Typography2>
      )}
    </ContextualHelp>
  );

export const CheckableExpandableSearchFilterGroup = (props: Props) => {
  const {
    groupConfig,
    groupValues = [],
    eventingData,
    onFilterChange,
    unappliedFilters,
    isChip,
    shouldHideGroupLabel,
  } = props;
  const [shouldShowAllItems, setShouldShowAllItems] = React.useState(groupConfig.showAllItems);
  const [searchInputValue, setSearchInputValue] = React.useState('');
  const isEnterpriseAdminSearch = useEnterpriseAdminSearchContext();
  const groupShouldExpandCollapse = !groupConfig.showAllItems;
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
  const track = useRetracked();

  const filterGroupName = groupConfig.getName();
  const filterGroupKey = groupConfig.property;

  const filteredGroupValues = React.useMemo(
    () => groupValues?.filter(filterFilterOptions(debouncedSearchQuery, isEnterpriseAdminSearch)),
    [groupValues, debouncedSearchQuery, isEnterpriseAdminSearch]
  );
  const sortedFilterOptions = useSortedItems(filteredGroupValues ?? [], filterGroupKey, { sortByCount: true });
  const itemsPreviewList = sortedFilterOptions?.slice(0, FILTER_PREVIEW_COUNT);

  const debouncedRefine = useDebounce(() => {
    setDebouncedSearchQuery(searchInputValue);
    track({
      action: 'search',
      trackingName: 'filter_query_change',
      trackingData: {
        newSearchValue: removeWhiteSpace(searchInputValue),
        filterCategory: filterGroupKey,
        page: window?.location.pathname,
      },
    });
  }, 250);

  const onClear = React.useCallback<() => $TSFixMe>(() => {
    setDebouncedSearchQuery('');
    setShouldShowAllItems(false);
  }, []);

  const handleFilterCheck = (value: string) => {
    onFilterChange({ [filterGroupKey]: [...unappliedFilters, value] });
  };

  const handleFilterUncheck = (value: string) => {
    onFilterChange({ [filterGroupKey]: unappliedFilters.filter((filterValue) => filterValue !== value) });
  };

  if (groupValues.length === 0) {
    return null;
  }

  if (filterGroupKey === IS_PART_OF_COURSERA_PLUS) {
    return (
      <CourseraPlusSearchFilter
        name={filterGroupName}
        items={groupValues}
        addFilters={handleFilterCheck}
        removeFilters={handleFilterUncheck}
        eventingData={eventingData}
        isRefined={unappliedFilters.includes(`${IS_PART_OF_COURSERA_PLUS}:true`)}
      />
    );
  }

  return (
    <div data-testid={`search-filter-group-${filterGroupName}`}>
      <CheckboxGroup
        label={
          filterGroupKey === IS_PART_OF_COURSERA_PLUS || shouldHideGroupLabel
            ? undefined
            : getCustomFilterGroupLabel()[filterGroupKey] || groupConfig.getName()
        }
        renderLabel={
          (filterGroupKey === 'productTypeDescription' || groupConfig.getToolTip?.()) && !shouldHideGroupLabel
            ? getFilterGroupTooltipRenderer(groupConfig)
            : undefined
        }
        aria-label={_t('Filter by #{filterGroupName}', { filterGroupName })}
        css={styles.checkboxGroup}
      >
        <>
          {/* only show search when relevant */}
          {groupShouldExpandCollapse && groupValues.length > THRESHOLD_FOR_FILTER_GROUP_BECOMING_SEARCHABLE && (
            <Search
              hideSearchButton
              placeholder={_t('Search #{filterGroupName} (#{count})', {
                filterGroupName,
                count: groupValues.length,
              })}
              value={searchInputValue}
              onChange={(value) => {
                debouncedRefine();
                setSearchInputValue(value);
              }}
              onClear={onClear}
              css={styles.search}
            />
          )}

          {/* No results */}
          {debouncedSearchQuery && sortedFilterOptions.length === 0 && (
            <Typography2 component="p" variant="bodyPrimary">
              {_t('No results found')}
            </Typography2>
          )}
        </>
        <>
          {/* list of checkboxes */}
          {(shouldShowAllItems ? sortedFilterOptions ?? [] : itemsPreviewList).map((filterItem) => {
            const { label, value, count } = filterItem;
            return (
              <SearchFilterItem
                {...filterItem}
                label={label}
                key={label}
                value={`${filterGroupKey}:${value}`}
                isRefined={unappliedFilters.includes(`${filterGroupKey}:${value}`)}
                count={count}
                filterCategoryName={filterGroupName}
                addFilters={handleFilterCheck}
                removeFilters={handleFilterUncheck}
                eventingData={eventingData}
                shouldAlignResultsRight={isChip}
              />
            );
          })}

          {/* show "Show more" button if there are more than FILTER_PREVIEW_COUNT items and the group is not expanded */}
          {groupShouldExpandCollapse && sortedFilterOptions.length > FILTER_PREVIEW_COUNT && (
            <Button
              data-testid="expand-filter-items-button"
              trackingName={`${shouldShowAllItems ? 'collapse' : 'expand'}_filter_items_button_${filterGroupKey}`}
              withVisibilityTracking={false}
              requireFullyVisible={false}
              component={TrackedButton}
              icon={shouldShowAllItems ? <ChevronUpIcon size="small" /> : <ChevronDownIcon size="small" />}
              iconPosition="before"
              variant="ghost"
              onClick={() => setShouldShowAllItems(!shouldShowAllItems)}
              aria-label={_t('Show #{direction} #{filterGroupName} options', {
                filterGroupName,
                direction: shouldShowAllItems ? 'less' : 'more',
              })}
              css={styles.showMoreButton}
            >
              {shouldShowAllItems
                ? _t('Show less')
                : _t('Show #{countMore} more', { countMore: sortedFilterOptions.length - FILTER_PREVIEW_COUNT })}
            </Button>
          )}
        </>
      </CheckboxGroup>
    </div>
  );
};

export default CheckableExpandableSearchFilterGroup;
